import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { getApiUrl, fetchWithAuth } from '../../../api'

interface MemberListItem {
  id: number
  login_id: string
  name: string
  role: number
  last_attendance: string | null
  total_points: number
}

interface MemberListResponse {
  total: number
  members: MemberListItem[]
}

export default function AdminMemberList() {
  const navigate = useNavigate()
  const [members, setMembers] = useState<MemberListItem[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [loginIdSearch, setLoginIdSearch] = useState('')
  const [nameSearch, setNameSearch] = useState('')
  const [loginIdInput, setLoginIdInput] = useState('')
  const [nameInput, setNameInput] = useState('')
  const [skip, setSkip] = useState(0)
  const limit = 20

  const fetchMembers = useCallback(async (loginId: string, name: string, skipVal: number) => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      if (loginId) params.append('login_id', loginId)
      if (name) params.append('name', name)
      params.append('skip', String(skipVal))
      params.append('limit', String(limit))

      const res = await fetchWithAuth(getApiUrl(`/marubit/api/member/admin/list?${params.toString()}`))
      if (!res.ok) throw new Error('조회 실패')
      const data: MemberListResponse = await res.json()
      setMembers(data.members)
      setTotal(data.total)
    } catch {
      alert('목록을 불러오는 데 실패했습니다.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchMembers(loginIdSearch, nameSearch, skip)
  }, [loginIdSearch, nameSearch, skip, fetchMembers])

  const handleSearch = () => {
    setLoginIdSearch(loginIdInput)
    setNameSearch(nameInput)
    setSkip(0)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch()
  }

  const handleResetSearch = () => {
    setLoginIdInput('')
    setNameInput('')
    setLoginIdSearch('')
    setNameSearch('')
    setSkip(0)
  }

  const handleDelete = async (e: React.MouseEvent, id: number, name: string) => {
    e.stopPropagation()
    if (!window.confirm(`"${name}" 회원을 삭제하시겠습니까?`)) return
    try {
      const token = localStorage.getItem('access_token')
      const res = await fetch(getApiUrl(`/api/member/admin/${id}`), {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) throw new Error('삭제 실패')
      fetchMembers(loginIdSearch, nameSearch, skip)
    } catch {
      alert('삭제에 실패했습니다.')
    }
  }

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
  }

  const getRoleLabel = (role: number) => {
    switch (role) {
      case 1: return { label: '관리자', cls: 'bg-[#fff3e0] text-[#e67e22]' }
      case 0: return { label: '일반', cls: 'bg-[#e8f5e9] text-[#388e3c]' }
      default: return { label: String(role), cls: 'bg-gray-100 text-gray-500' }
    }
  }

  const totalPages = Math.ceil(total / limit)
  const currentPage = Math.floor(skip / limit) + 1

  const handlePageChange = (page: number) => {
    setSkip((page - 1) * limit)
  }

  return (
    <div className="bg-[#f5f2f0] min-h-screen p-8">
      <div className="max-w-[1200px] mx-auto">

        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-[22px] font-extrabold text-[#2a1f1a] mb-1">회원 관리</h1>
            <p className="text-[13px] text-gray-400">
              {new Date().toLocaleDateString('ko-KR', {
                year: 'numeric', month: 'long', day: 'numeric', weekday: 'long',
              })}
            </p>
          </div>
          <div className="text-[13px] text-gray-400 font-medium mt-1">
            총 <span className="text-[#887267] font-bold">{total}</span>명
          </div>
        </div>

        {/* Search Bar */}
        <div className="bg-white rounded-2xl border border-[#e8e0dc] px-5 py-4 mb-4 shadow-[0_2px_12px_rgba(136,114,103,0.06)] flex flex-wrap gap-3 items-end">
          <div className="flex flex-col gap-1">
            <label className="text-[11px] text-[#5a3d32] font-medium">아이디</label>
            <input
              type="text"
              value={loginIdInput}
              onChange={e => setLoginIdInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="아이디 검색"
              className="border border-[#e8e0dc] rounded-lg px-3 py-2 text-[13px] text-[#2a1f1a] outline-none focus:border-[#887267] w-[180px] transition-colors"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[11px] text-[#5a3d32] font-medium">이름</label>
            <input
              type="text"
              value={nameInput}
              onChange={e => setNameInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="이름 검색"
              className="border border-[#e8e0dc] rounded-lg px-3 py-2 text-[13px] text-[#2a1f1a] outline-none focus:border-[#887267] w-[180px] transition-colors"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleSearch}
              className="bg-[#887267] text-white text-[13px] font-semibold px-4 py-2 rounded-lg border-none cursor-pointer hover:bg-[#6b5248] transition-colors h-[38px]"
            >
              검색
            </button>
            <button
              onClick={handleResetSearch}
              className="bg-white text-[#887267] text-[13px] font-semibold px-4 py-2 rounded-lg border border-[#e8e0dc] cursor-pointer hover:bg-[#f5f2f0] transition-colors h-[38px]"
            >
              초기화
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl border border-[#e8e0dc] overflow-hidden shadow-[0_2px_12px_rgba(136,114,103,0.06)]">
          {loading ? (
            <div className="min-h-[300px] flex items-center justify-center text-gray-300 text-[13px]">
              불러오는 중...
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="bg-[#ede0d8] border-b border-[#e8e0dc]">
                  <th className="text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[48px]">ID</th>
                  <th className="text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium">아이디</th>
                  <th className="text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium">이름</th>
                  <th className="text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[72px]">권한</th>
                  <th className="hidden md:table-cell text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[120px]">마지막 출석</th>
                  <th className="hidden sm:table-cell text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[90px]">포인트</th>
                  <th className="text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[110px]"></th>
                </tr>
              </thead>
              <tbody>
                {members.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-12 text-gray-300 text-[13px]">
                      {loginIdSearch || nameSearch ? '검색 결과가 없습니다.' : '등록된 회원이 없습니다.'}
                    </td>
                  </tr>
                ) : members.map(member => {
                  const role = getRoleLabel(member.role)
                  return (
                    <tr
                      key={member.id}
                      className="border-t border-[#f5f2f0] hover:bg-[#fdf8f0] transition-colors cursor-pointer"
                      onClick={() => navigate(`/admin/member/${member.id}`)}
                    >
                      <td className="px-4 py-3 text-[13px] text-gray-400 whitespace-nowrap">{member.id}</td>
                      <td className="px-4 py-3 text-[13px] font-medium text-[#2a1f1a] whitespace-nowrap">{member.login_id}</td>
                      <td className="px-4 py-3 text-[13px] text-[#2a1f1a] whitespace-nowrap">{member.name}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`text-[11px] px-2 py-0.5 rounded font-medium ${role.cls}`}>
                          {role.label}
                        </span>
                      </td>
                      <td className="hidden md:table-cell px-4 py-3 text-[12px] text-gray-400 whitespace-nowrap">
                        {formatDate(member.last_attendance)}
                      </td>
                      <td className="hidden sm:table-cell px-4 py-3 text-[13px] text-[#887267] font-semibold whitespace-nowrap">
                        {member.total_points.toLocaleString()}p
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap" onClick={e => e.stopPropagation()}>
                        <div className="flex gap-1.5">
                          <button
                            onClick={() => navigate(`/admin/member/${member.id}`)}
                            className="text-[12px] text-[#887267] hover:text-[#6b5248] border border-[#e8d8ce] rounded px-2 py-1 bg-transparent cursor-pointer whitespace-nowrap transition-colors"
                          >
                            수정
                          </button>
                          <button
                            onClick={e => handleDelete(e, member.id, member.name)}
                            className="text-[12px] text-red-400 hover:text-red-600 border border-red-100 rounded px-2 py-1 bg-transparent cursor-pointer whitespace-nowrap transition-colors"
                          >
                            삭제
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-1.5 mt-5">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="w-8 h-8 rounded-lg text-[12px] font-medium border border-[#e8e0dc] bg-white text-[#887267] disabled:text-gray-300 disabled:cursor-not-allowed hover:bg-[#f5f2f0] transition-colors cursor-pointer"
            >
              ‹
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
              .reduce<(number | '...')[]>((acc, p, idx, arr) => {
                if (idx > 0 && typeof arr[idx - 1] === 'number' && (p as number) - (arr[idx - 1] as number) > 1) {
                  acc.push('...')
                }
                acc.push(p)
                return acc
              }, [])
              .map((p, idx) =>
                p === '...' ? (
                  <span key={`ellipsis-${idx}`} className="w-8 h-8 flex items-center justify-center text-[12px] text-gray-300">…</span>
                ) : (
                  <button
                    key={p}
                    onClick={() => handlePageChange(p as number)}
                    className={`w-8 h-8 rounded-lg text-[12px] font-medium border transition-colors cursor-pointer ${
                      currentPage === p
                        ? 'bg-[#887267] text-white border-[#887267]'
                        : 'bg-white text-[#5a3d32] border-[#e8e0dc] hover:bg-[#f5f2f0]'
                    }`}
                  >
                    {p}
                  </button>
                )
              )}
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="w-8 h-8 rounded-lg text-[12px] font-medium border border-[#e8e0dc] bg-white text-[#887267] disabled:text-gray-300 disabled:cursor-not-allowed hover:bg-[#f5f2f0] transition-colors cursor-pointer"
            >
              ›
            </button>
          </div>
        )}

      </div>
    </div>
  )
}